import { Box, InputCheckBox, Text } from '@factorialco/deprecated-design-system'
import { TaskStatusFragment } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { useCreateTaskDocument } from 'modules/bookkeepers/hooks/useCreateTaskDocument'
import { useDeleteTaskDocument } from 'modules/bookkeepers/hooks/useDeleteTaskDocument'
import type { IncidenceTaskDocument } from 'modules/bookkeepers/hooks/useGetIncidenceTaskDocuments'
import type TaskType from 'modules/bookkeepers/store/models/TaskType'

import TaskDocument from './TaskDocument'

interface Props {
  incidenceId: string
  taskType: TaskType
  onSelect: (newStatus: boolean, taskTypeId: string, taskType?: TaskStatusFragment) => void
  onDelete: () => void
  taskStatus?: TaskStatusFragment
  disabled: boolean
  document?: IncidenceTaskDocument
}

const setAccessName = (taskStatus: TaskStatusFragment | undefined) => {
  if (!taskStatus) return
  const completed = taskStatus.completed
  if (!completed) return

  const date = moment(taskStatus.updatedAt).format('MMM DD, YYYY, HH:mm')
  const doneByText = i18n.t('bookkeepers.incidence.note.tasks.status.done_by')

  if (taskStatus.accessName) {
    return `${date} - ${doneByText} ${taskStatus.accessName}`
  } else {
    return `${date} - ${doneByText} ${i18n.t('bookkeepers.incidence.note.deleted_user')}`
  }
}

const IncidenceTaskItem = ({
  incidenceId,
  taskType,
  onSelect,
  onDelete,
  taskStatus,
  disabled,
  document,
}: Props) => {
  const checked = taskStatus ? taskStatus?.completed : false
  const accessName = setAccessName(taskStatus)
  const { create, loading: createLoading } = useCreateTaskDocument()
  const { deleteDocument, loading: deleteLoading } = useDeleteTaskDocument()

  const handleDocumentDrop = async (files: File[]) => {
    const result = await create({ taskTypeId: taskType.id, incidenceId, file: files[0] })

    const handled = handleResult(result, () => undefined)

    if (handled.success && !checked) {
      onSelect(true, taskType.id, taskStatus)
    }
  }

  const handleDelete = async (id: string) => {
    const result = await deleteDocument({ id }, incidenceId)
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      await onDelete()
    }
  }

  return (
    <Box component="li" flexDirection="column" gap="s8">
      <Box flexDirection="row" gap="s8">
        <Box flexNoShrink paddingTop="s4">
          <InputCheckBox
            onClick={() => onSelect(!checked, taskType.get('id'), taskStatus)}
            checked={checked}
            disabled={disabled}
          />
        </Box>
        <Box flexDirection="column">
          <Text.Normal>{taskType.get('translated_title')}</Text.Normal>
          {accessName && (
            <Box alignItems="center">
              <Text.Normal size="100" color="grey700">
                {accessName}
              </Text.Normal>
            </Box>
          )}
        </Box>
      </Box>
      {!!(taskType.get('folder_id') || taskType.get('folder_type')) && (
        <TaskDocument
          document={document}
          onDocumentDrop={handleDocumentDrop}
          onDelete={handleDelete}
          deleting={deleteLoading}
          creating={createLoading}
        />
      )}
    </Box>
  )
}

export default observer(IncidenceTaskItem)
